import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Form,
  Input,
  Radio,
  Typography,
  DatePicker,
  Select,
  Divider,
  Button,
  AutoComplete,
} from "antd";
import moment from "moment";
import debounce from "lodash/debounce";

import productOfferFree from "../../../assets/productOfferFree.png";
import Popup from "../../../components/Popup";
import { DateFormat, getApi, saveApi } from "../../../helpers";
import { PlusOutlined } from "@ant-design/icons";
import TagPopup from "../TagPopup";
import { StoreContext } from "../../../context";
import "./index.css";

export default function OfferPopup({
  visible = false,
  title = "",
  data = {},
  cancelHandler,
  saveHandler,
}) {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [offerBy, setOfferBy] = useState("");
  const [options, setOptions] = useState([]);
  const [stores, setStores] = useState([]);
  const [variantId, setVariantId] = useState(0);
  const [tags, setTags] = useState([]);
  const [popup, setPopup] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (data.amount_discount) {
      setOfferBy("amount_discount");
    } else if (data.category_id) {
      setOfferBy("category_id");
      getOptionsHandler();
    } else if (data.variant_id) {
      setVariantId(data.variant_id);
      setOfferBy("variant_id");
    } else {
      setOfferBy("percent_discount");
    }
    getTags();
    getStores();
  }, []);

  useEffect(() => {
    if (storeId) {
      form.setFieldsValue({ store_id: storeId });
    }
  }, [storeId, form]);

  async function getStores() {
    const res = await getApi({}, "getStores", "dashboard/");
    if (res.data && res.data.length) {
      setStores(res.data);
    }
  }

  async function offerByChangeHandler(e) {
    if (e.target.value) {
      setOptions([]);
      setOfferBy(e.target.value);
      if (e.target.value === "variant_id") {
        setOptions([{ label: "Type to Search Product", disabled: true }]);
      } else if (e.target.value === "category_id") {
        getOptionsHandler("getCategory");
      }
    }
  }

  async function getOptionsHandler(apiName = "getCategory", keyword = "", type="admin") {
    setLoading(true);
    setOptions([{ label: "Searching...", disabled: true }]);
    const res = await getApi({ size: 100, keyword: keyword }, apiName);
    if (res.data && res.data.length) {
      setOptions(res.data);
    } else {
      setOptions([{ label: "No Such Product", disabled: true }]);
    }
    setLoading(false);
  }

  async function getTags() {
    setLoading(true);
    const res = await getApi({ size: 100, type:"admin" }, "getTag");
    if (res.data) {
      let opts = [];
      await res.data.map((tag) => {
        opts.push({
          value: tag.tag_id,
          label: tag.tag_name,
        });
      });
      setTags(opts);
    }
    setLoading(false);
  }

  const debouncedSearch = useCallback(
    debounce((nextValue) => getOptionsHandler("getVariants", nextValue), 1200),
    []
  );

  async function saveTag(values) {
    setLoading(true);
    values = { ...values, getId: true };
    // console.log(values);
    const res = await saveApi(values, "Added Tag", "createTag");
    if (res.tag_id) {
      setPopup(false);
      let allTags = [{ value: res.tag_id, label: values.name }, ...tags];
      setTags(allTags);
    }
    setLoading(false);
  }

  function saveOffer() {
    form
      .validateFields()
      .then((values) => {
        form.resetFields();
        values = {
          ...values,
          valid_from: DateFormat(values.validity[0], "YYYY-MM-DD"),
          valid_till: DateFormat(values.validity[1], "YYYY-MM-DD"),
        };
        if (values.percent_discount) {
          values = { ...values, is_percent: 1 };
        }
        if (values.variant_name) {
          values = { ...values, variant_id: variantId };
        }
        saveHandler(values);
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  }

  return (
    <Popup
      width="55%"
      title={title}
      visible={visible}
      cancelHandler={() => (form.resetFields(), cancelHandler())}
      preSaveCheck={() =>
        form
          .validateFields()
          .then(() => true)
          .catch(() => false)
      }
      saveHandler={saveOffer}
    >
      {popup && (
        <TagPopup
          title="Add Tag"
          visible
          loading={loading}
          cancelHandler={() => setPopup(false)}
          saveHandler={saveTag}
        />
      )}
      <Form
        className="offer-popup-form"
        requiredMark={false}
        form={form}
        layout="vertical"
        name="offer"
        initialValues={{ ...data, store_id: storeId || data.store_id }}
      >
        <div className="offer-popup-section">
          <Typography.Text className="offer-popup-section-title">
            Offer Information
          </Typography.Text>
          <div className="form-row">
            <div className="form-group">
              <Form.Item
                name="name"
                label="Name"
                rules={[
                  {
                    required: true,
                    message: "Please enter the name of offer!",
                  },
                ]}
              >
                <Input placeholder="Ex. 10% off on Dairy Products" />
              </Form.Item>
            </div>
            <div className="form-group">
              <Form.Item
                name="coupon_code"
                label="Coupon Code"
                rules={[
                  {
                    required: true,
                    message: "Please enter the coupon code of offer!",
                  },
                ]}
              >
                <Input placeholder="Ex. DAIRY10" />
              </Form.Item>
            </div>
          </div>
        </div>

        <div className="offer-popup-section">
          <Typography.Text className="offer-popup-section-title">
            Discount Configuration
          </Typography.Text>
          <div className="form-row">
            <div className="form-group full-width">
              <Typography.Text className="offer-popup-subtitle">Offer By</Typography.Text>
              <Form.Item
                style={{ marginTop: 5, marginBottom: 8 }}
                name="offerBy"
                initialValue={
                  offerBy
                    ? offerBy
                    : data.amount_discount
                    ? "amount_discount"
                    : data.category_id
                    ? "category_id"
                    : data.variant_id
                    ? "variant_id"
                    : "percent_discount"
                }
              >
                <Radio.Group onChange={offerByChangeHandler}>
                  <Radio value={"percent_discount"}>Percent</Radio>
                  <Radio value={"amount_discount"}>Amount</Radio>
                  <Radio value={"category_id"}>Category</Radio>
                  <Radio value={"variant_id"}>Product</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          </div>

          {offerBy === "category_id" && (
            <div className="form-row">
              <div className="form-group">
                <Form.Item
                  name={offerBy}
                  label="Select Category"
                  rules={[
                    {
                      required: true,
                      message: "Please select the Category for offer!",
                    },
                  ]}
                >
                  <Select
                    loading={loading}
                    placeholder="Ex. Dairy"
                    showSearch
                    dropdownStyle={{ maxHeight: 100 }}
                    listHeight={96}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    filterSort={(optionA, optionB) =>
                      optionA.children
                        .toLowerCase()
                        .localeCompare(optionB.children.toLowerCase())
                    }
                  >
                    {options.map((category) => (
                      <Select.Option
                        key={category.category_id}
                        value={category.category_id}
                      >
                        {category.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
              <div className="form-group">
                <Form.Item
                  name="percent_discount"
                  label="Discount (in %)"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value > 0) {
                          if (
                            value > 100 ||
                            parseInt(value).toString().length < value.length
                          ) {
                            return Promise.reject(
                              new Error("Percentage between 1-100 only !")
                            );
                          }
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Percent discount should be number!")
                        );
                      },
                    },
                  ]}
                >
                  <Input placeholder="Ex. 20 %" />
                </Form.Item>
              </div>
            </div>
          )}

          {offerBy.includes("discount") ? (
            <div className="form-row">
              <div className="form-group">
                <Form.Item
                  name={offerBy}
                  label={
                    offerBy === "percent_discount"
                      ? "Discount (in %)"
                      : "Discount (in ₹)"
                  }
                  initialValue={
                    offerBy === "percent_discount"
                      ? data.percent_discount
                      : data.amount_discount
                  }
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value > 0) {
                          if (
                            offerBy === "percent_discount" &&
                            (value > 100 ||
                              parseInt(value).toString().length < value.length)
                          ) {
                            return Promise.reject(
                              new Error("Percentage between 1-100 only !")
                            );
                          } else if (
                            offerBy === "amount_discount" &&
                            // !value.includes("/A-Za-z/") &&
                            parseFloat(value).toFixed(2).length < value.length
                          ) {
                            return Promise.reject(
                              new Error("Numbers upto 2 decimals places !")
                            );
                          }
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Discount should be a number!")
                        );
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder={
                      offerBy === "percent_discount" ? "Ex. 20 %" : "Ex. ₹ 200"
                    }
                  />
                </Form.Item>
              </div>
              {(offerBy === "percent_discount" || offerBy === "category_id") && (
                <div className="form-group">
                  <Form.Item
                    name="upto"
                    label="Max Discount"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (value > 0) {
                            if (
                              parseFloat(value).toFixed(2).length < value.length ||
                              value.includes("/A-Za-z/")
                            ) {
                              return Promise.reject(
                                new Error("Numbers upto 2 decimal places")
                              );
                            }
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("Max discount should be a number!")
                          );
                        },
                      },
                    ]}
                  >
                    <Input placeholder="Ex. upto ₹ 200" />
                  </Form.Item>
                </div>
              )}
            </div>
          ) : (
            offerBy === "variant_id" && (
              <div className="form-row">
                <div className="form-group full-width offer-popup-product-group">
                  <Form.Item
                    name="variant_name"
                    label="Select Product"
                    rules={[
                      {
                        validator: () => {
                          if (variantId) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("Please select the Product for offer!")
                          );
                        },
                      },
                    ]}
                  >
                    <AutoComplete
                      showArrow
                      loading={loading}
                      dropdownStyle={{ maxHeight: 100 }}
                      listHeight={96}
                      options={options.map((v) => {
                        if (v.label) {
                          return v;
                        }
                        return (v = {
                          value: v.product_name + " - " + v.variant_name,
                          key: v.variant_id,
                        });
                      })}
                      onSelect={(v, vObj) => setVariantId(vObj.key)}
                      onSearch={(value) =>
                        value.length > 1 && debouncedSearch(value)
                      }
                      placeholder="Ex. Milk - 1 Ltr"
                    />
                  </Form.Item>
                  <div className="offer-popup-product-badge">
                    <img src={productOfferFree} alt="product offer" height={32} />
                  </div>
                </div>
              </div>
            )
          )}

          <div className="form-row">
            <div className="form-group">
              <Form.Item
                name="min_amount"
                label="Min Order Amount"
                initialValue={data.min_amount}
                rules={[
                  {
                    validator: (_, value) => {
                      if (value > 0) {
                        if (
                          parseFloat(value).toFixed(2).length < value.length ||
                          value.includes("/A-Za-z/")
                        ) {
                          return Promise.reject(
                            new Error("Numbers upto 2 decimal places")
                          );
                        }
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Min Order Amount should be number!")
                      );
                    },
                  },
                ]}
              >
                <Input placeholder="Ex. ₹ 200" />
              </Form.Item>
            </div>
            <div className="form-group">
              <Form.Item
                label="Validity"
                name="validity"
                rules={[
                  {
                    required: true,
                    message: "Please select the validity for offer!",
                  },
                ]}
                initialValue={
                  data.valid_from &&
                  data.valid_till && [
                    moment(data.valid_from),
                    moment(data.valid_till),
                  ]
                }
              >
                <DatePicker.RangePicker
                  style={{ width: "100%" }}
                  placeholder={["22-06-2021", "28-06-2021"]}
                  format="DD-MM-YYYY"
                />
              </Form.Item>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <Form.Item name="tag_id" label="Tag">
                <Select
                  options={tags}
                  placeholder="Ex. EARLYBIRD"
                  allowClear
                  listHeight={100}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: "4px 0" }} />
                      <div className="offer-popup-tag-footer">
                        <Button
                          type="dashed"
                          onClick={() => setPopup(true)}
                          block
                          icon={<PlusOutlined />}
                        >
                          Add Tag
                        </Button>
                      </div>
                    </>
                  )}
                ></Select>
              </Form.Item>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <Form.Item
                className="offer-popup-description-item"
                name="description"
                label="Description"
              >
                <Input.TextArea
                  showCount
                  maxLength={255}
                  placeholder="Ex. All types of dairy products can avail 10% off."
                />
              </Form.Item>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <Form.Item
                className="offer-popup-store-item"
                name="store_id"
                label="Store"
                rules={[
                  {
                    required: true,
                    message: "Please select store!",
                  },
                ]}
              >
                <Select
                  placeholder="Select store"
                  showSearch
                  optionFilterProp="children"
                  disabled={!!storeId}
                >
                  {stores.map((store) => (
                    <Select.Option key={store.store_id} value={store.store_id}>
                      {store.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </div>
        </div>
      </Form>
    </Popup>
  );
}
